import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Table, TableHead, TableBody, TableRow, TableCell, Chip, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import MaybeLayout from '../../components/layout/MaybeLayout';
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import DevicesIcon from "@mui/icons-material/Devices";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getScreens } from "../../Redux/Actions/screenAction";
import { getStores } from "../../Redux/Actions/storeAction";
import { getTunnelStatus, getWgHealth, getAppVersions } from "../../Redux/Actions/peerAction";
import ApkManager from "./ApkManager";
import "./provisioning.css";

const CustomTableCell = styled(TableCell)(() => ({
  color: '#15192C', fontSize: '13px', fontWeight: '600',
}));

function Provisioning({ user, embedded = false }) {
  const dispatch = useDispatch();
  const { screens, screenLoading } = useSelector(s => s.Screen);
  const { stores } = useSelector(s => s.Store);
  const { tunnelStatus, wgHealth, appVersions, loading: peerLoading } = useSelector(s => s.Peer);
  const [filter, setFilter] = useState('all'); // all, connected, disconnected, unassigned, pending

  useEffect(() => {
    dispatch(getScreens(user?._id));
    dispatch(getStores(user?._id));
    dispatch(getTunnelStatus());
    dispatch(getWgHealth());
    dispatch(getAppVersions());
  }, []);

  const refresh = () => {
    dispatch(getTunnelStatus());
    dispatch(getWgHealth());
    dispatch(getScreens(user?._id));
  };

  // Stats
  const realDevices = screens.filter(s => !s.mac?.startsWith('PLH_'));
  const placeholders = screens.filter(s => s.mac?.startsWith('PLH_'));
  const wgConnected = realDevices.filter(s => s.wgStatus === 'connected').length;
  const wgDisconnected = realDevices.filter(s => s.wgStatus === 'disconnected').length;
  const wgUnassigned = placeholders.length;
  const provActive = realDevices.filter(s => s.provisioningStatus === 'active').length;
  const provPending = realDevices.filter(s => !s.provisioningStatus || s.provisioningStatus === 'pending').length;

  // Filter
  const filtered = filter === 'all' ? screens
    : filter === 'connected' ? screens.filter(s => s.wgStatus === 'connected')
    : filter === 'disconnected' ? screens.filter(s => s.wgStatus === 'disconnected' && !s.mac?.startsWith('PLH_'))
    : filter === 'unassigned' ? placeholders
    : filter === 'pending' ? screens.filter(s => !s.provisioningStatus || s.provisioningStatus === 'pending')
    : screens;

  const getStoreLabel = (screen) => {
    if (!screen.store) return '-';
    const st = typeof screen.store === 'object' ? screen.store : stores?.find(s => s._id === screen.store);
    return st ? `${st.state || ''} ${st.town || ''}`.trim() || st.tljNo || '-' : '-';
  };

  return (
    <MaybeLayout embedded={embedded} title="Device Provisioning" user={user}>
      <Box sx={{ p: 2 }}>

        {/* Header + Refresh */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#15192C' }}>
            Device Provisioning Dashboard
          </Typography>
          <button onClick={refresh} className="prov-refresh-btn">
            <RefreshIcon style={{ fontSize: 16 }} /> Refresh
          </button>
        </Box>

        {/* WG Server Health */}
        {wgHealth?.available !== undefined && (
          <Box sx={{ mb: 2, p: 1.5, background: wgHealth.available ? '#f0fdf4' : '#fef2f2', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
            {wgHealth.available
              ? <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
              : <WarningAmberIcon sx={{ color: '#ef4444', fontSize: 18 }} />
            }
            <Typography sx={{ fontSize: 12, color: wgHealth.available ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              WG Server: {wgHealth.available ? `Online — ${wgHealth.peers || 0} peers, ${wgHealth.ipPool?.free || 0} IPs available` : 'Offline — check wg-server'}
            </Typography>
          </Box>
        )}

        {/* Stat Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Total Devices', value: realDevices.length, icon: <DevicesIcon />, bg: '#EDE8FF', color: '#551FFF', filterKey: 'all' },
            { label: 'WG Connected', value: wgConnected, icon: <WifiIcon />, bg: '#f0fdf4', color: '#22c55e', filterKey: 'connected' },
            { label: 'WG Disconnected', value: wgDisconnected, icon: <WifiOffIcon />, bg: '#fef2f2', color: '#ef4444', filterKey: 'disconnected' },
            { label: 'Unassigned', value: wgUnassigned, icon: <LinkOffIcon />, bg: '#fffbeb', color: '#d97706', filterKey: 'unassigned' },
          ].map(({ label, value, icon, bg, color, filterKey }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Box onClick={() => setFilter(filterKey)}
                sx={{ p: 2, background: 'white', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', cursor: 'pointer',
                  border: filter === filterKey ? `2px solid ${color}` : '2px solid transparent', transition: 'border 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ background: bg, borderRadius: 8, p: 0.8, display: 'flex', color }}>{React.cloneElement(icon, { sx: { fontSize: 20 } })}</Box>
                  <Typography sx={{ fontSize: 11, color: '#92959E', fontWeight: 600 }}>{label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color }}>{value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* APK Versions — now handled by ApkManager below */}

        {/* APK Version Management */}
        <Box sx={{ mb: 2, p: 2, background: 'white', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <ApkManager appVersions={appVersions || []} screens={screens} />
        </Box>

        {/* Device Table */}
        <Box sx={{ background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          {screenLoading || peerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={40} sx={{ color: '#551FFF' }} />
            </Box>
          ) : (
            <>
              {/* Filter chips */}
              <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
                {['all', 'connected', 'disconnected', 'unassigned', 'pending'].map(f => (
                  <Chip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} size="small"
                    onClick={() => setFilter(f)}
                    variant={filter === f ? 'filled' : 'outlined'}
                    sx={{ fontSize: 11, ...(filter === f ? { background: '#551FFF', color: 'white' } : {}) }}
                  />
                ))}
                <Typography sx={{ fontSize: 11, color: '#aaa', ml: 'auto', alignSelf: 'center' }}>{filtered.length} devices</Typography>
              </Box>

              {/* Desktop Table */}
              <Box className="prov-desktop-view">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderBottom: '2px solid #551FFF' }}>
                      <CustomTableCell>Name</CustomTableCell>
                      <CustomTableCell>Store</CustomTableCell>
                      <CustomTableCell>MAC</CustomTableCell>
                      <CustomTableCell align="center">WG Status</CustomTableCell>
                      <CustomTableCell align="center">WG IP</CustomTableCell>
                      <CustomTableCell align="center">Provisioning</CustomTableCell>
                      <CustomTableCell align="center">Chipset</CustomTableCell>
                      <CustomTableCell align="center">App Ver</CustomTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map(screen => (
                      <TableRow key={screen._id} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{screen.name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: '#666' }}>{getStoreLabel(screen)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: screen.mac?.startsWith('PLH_') ? '#ccc' : '#333' }}>
                          {screen.mac?.startsWith('PLH_') ? '(placeholder)' : screen.mac || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {screen.wgStatus === 'connected'
                            ? <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 600 }}>● Connected</span>
                            : screen.wgStatus === 'disconnected'
                            ? <span style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>● Disconnected</span>
                            : <span style={{ color: '#ddd', fontSize: 12 }}>—</span>
                          }
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>
                          {screen.wgIp || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <span className={`prov-badge prov-badge--${screen.provisioningStatus || 'pending'}`}>
                            {screen.provisioningStatus || 'pending'}
                          </span>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 11, color: '#888' }}>
                          {screen.chipset || '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 11, color: '#888' }}>
                          {screen.appVersion ? `v${screen.appVersion}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Mobile Cards */}
              <Box className="prov-mobile-view" sx={{ p: 1.5 }}>
                {filtered.map(screen => (
                  <Box key={screen._id} sx={{ p: 1.5, mb: 1, background: '#faf9ff', borderRadius: 10, border: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#15192C' }}>{screen.name || '-'}</Typography>
                      <span className={`prov-badge prov-badge--${screen.provisioningStatus || 'pending'}`}>
                        {screen.provisioningStatus || 'pending'}
                      </span>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 11 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#92959E' }}>Store</span>
                        <span style={{ color: '#333' }}>{getStoreLabel(screen)}</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#92959E' }}>WG</span>
                        <span style={{ color: screen.wgStatus === 'connected' ? '#22c55e' : screen.wgStatus === 'disconnected' ? '#f59e0b' : '#ccc' }}>
                          {screen.wgStatus === 'connected' ? '● Connected' : screen.wgStatus === 'disconnected' ? '● Disconnected' : '—'}
                          {screen.wgIp ? ` (${screen.wgIp})` : ''}
                        </span>
                      </Box>
                      {screen.chipset && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#92959E' }}>Chipset</span>
                          <span style={{ color: '#888' }}>{screen.chipset}</span>
                        </Box>
                      )}
                      {screen.appVersion && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#92959E' }}>App</span>
                          <span style={{ color: '#888' }}>v{screen.appVersion}</span>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </MaybeLayout>
  );
}

export default Provisioning;
